import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@silospot/i18n/react';
import { type LoginInput, loginSchema } from '@silospot/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  toast,
} from '@silospot/ui';
import { useMutation } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuthStore } from '../stores/auth-store';

export function LoginPage(): ReactElement {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: api.login,
    onSuccess: ({ user }) => {
      setUser(user);
      navigate('/dashboard');
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiError && error.status === 401
          ? t('errors.invalidCredentials')
          : t('errors.generic'),
      ),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('login.title')}</CardTitle>
        <CardDescription>
          {t('login.noAccount')}{' '}
          <Link to="/signup" className="text-primary hover:underline">
            {t('login.signupLink')}
          </Link>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('login.emailLabel')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('login.passwordLabel')}</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="current-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="text-right text-sm">
              <Link to="/forgot-password" className="text-muted-foreground hover:text-primary">
                {t('login.forgotPassword')}
              </Link>
            </div>
            <Button type="submit" className="w-full" disabled={mutation.isPending}>
              {t('login.submit')}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
